import type { NavItem } from '@agentic-flow-studio/shared/types/command-palette';
import {
  Anchor,
  Calendar,
  CheckSquare,
  IdCard,
  Layers,
  LayoutDashboard,
  Settings,
  UserCircle,
  Users,
} from 'lucide-react';

/**
 * Every navigable destination in the Skill Viper Portal, in the order the sidebar renders them.
 *
 * Single source of truth for both `AppSidebar` and the Ctrl/Cmd+K command palette — adding a
 * route here lights it up in both places at once. Order matters: the palette promotes the
 * leading entries to its root view, so keep the day-to-day surfaces first.
 */
export function buildNavItems(orgId: string, workspaceId: string): NavItem[] {
  const base = `/org/${orgId}/workspace/${workspaceId}`;

  return [
    {
      id: 'dashboard',
      title: 'Action Center',
      icon: LayoutDashboard,
      url: `${base}/dashboard`,
      section: 'Workspace',
      keywords: ['home', 'overview', 'action center', 'loops'],
    },
    {
      id: 'tasks',
      title: 'Tasks',
      icon: CheckSquare,
      url: `${base}/tasks`,
      section: 'Workspace',
      keywords: ['todo', 'work'],
      isActive: (pathname) => pathname.startsWith(`${base}/tasks`),
    },
    {
      id: 'contacts',
      title: 'Contacts',
      icon: Users,
      url: `${base}/contacts`,
      section: 'Workspace',
      keywords: ['people', 'customers'],
      isActive: (pathname) => pathname.startsWith(`${base}/contacts`),
    },
    {
      id: 'users',
      title: 'Users',
      icon: IdCard,
      url: `${base}/users`,
      section: 'Workspace',
      keywords: ['team', 'members', 'staff', 'colleagues', 'directory'],
      isActive: (pathname) => pathname.startsWith(`${base}/users`),
    },
    {
      id: 'anchors',
      title: 'Anchors',
      icon: Anchor,
      url: `${base}/anchors`,
      section: 'Workspace',
      keywords: ['entities', 'records', 'hooks'],
    },
    {
      id: 'calendar',
      title: 'Calendar',
      icon: Calendar,
      url: `${base}/calendar`,
      section: 'Workspace',
      keywords: ['schedule', 'events'],
    },
    {
      id: 'contact-types',
      title: 'Contact Types',
      icon: Layers,
      url: `${base}/settings/contact-types`,
      section: 'Settings',
      keywords: ['custom fields', 'schema'],
      isActive: (pathname) => pathname.startsWith(`${base}/settings/contact-types`),
    },
    {
      // The sidebar swaps this icon for the signed-in user's avatar; the palette uses it as-is.
      id: 'profile',
      title: 'Profile',
      icon: UserCircle,
      url: `${base}/profile`,
      section: 'Account',
      keywords: ['me', 'my account', 'avatar'],
    },
    {
      id: 'settings',
      title: 'Settings',
      icon: Settings,
      url: `${base}/settings`,
      section: 'Account',
      keywords: ['preferences', 'workspace', 'members', 'billing'],
    },
  ];
}
